<template>
  <div>
    <v-btn v-if="icon" @click="selectFile" color="accent" icon><v-icon>mdi-upload</v-icon></v-btn>
    <v-btn v-if="!icon" @click="selectFile" :class="'inset'" color="primary">{{ label ? label : $t('upload_image')}}</v-btn>
    <v-btn class="mt-3" @click="openSelectImageDialog" v-if="showSelectDialog">{{ $t('select_from_gallery') }}</v-btn>
    <div v-if="imageError && !openModal" class="error--text text-body-2 mt-2">{{ imageError }}</div>
    <input
      :ref="`input-${inputKey}`"
      :id="inputKey"
      @click="resetFileSelector"
      @change="setImage"
      type="file"
      name="image"
      :accept="accept">
    <v-dialog scrollable width="700" v-model="openModal">
      <v-card>
        <v-card-title class="headline">{{$t('image_upload')}}</v-card-title>
        <v-card-text>
          <vue-cropper
            :ref="`cropper-${inputKey}`"
            :src="this.src"
            :viewMode="1"
            :containerStyle="containerStyle"
            :zoomable="true"
            :aspect-ratio="ratio ? ratio : NaN"
            @error="imageError = $t('error_corrupted_image_file')"
          >
          </vue-cropper>
          <div v-if="imageError" class="error--text text-body-2 mt-3">{{ imageError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close">{{$t('close')}}</v-btn>
          <v-btn text @click="save" color="primary" :disabled="!!imageError">{{$t('save')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SelectImageDialog
      :display-select-image-dialog="displaySelectImageDialog"
      @update:displaySelectImageDialog="closeSelectImageDialog"
      @gallery-item-selected="handleGalleryItemSelected"
      :isAllowedEdit="isAllowed('gallery_edit')"
      :appTags="['st-portal-prod-image']"
    />
  </div>
</template>

<script>
import { SelectImageDialog } from '@scantrust/st-gallery'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { mapGetters } from 'vuex'

export default {
  name: 'ImageCropper',
  props: {
    src: {},
    icon: {},
    inputKey: {},
    ratio: {},
    label: {},
    showSelectDialog: {},
    maxFileMegabytes: {},
    accept: {
      type: String,
      default: 'image/png, image/jpeg, image/jpg, image/gif, image/tiff, image/tif, image/svg+xml'
    }
  },
  components: {
    VueCropper,
    SelectImageDialog
  },
  data () {
    return {
      openModal: false,
      type: null,
      croppedImg: null,
      containerStyle: {
        width: '100%',
        'max-height': '450px'
      },
      displaySelectImageDialog: false,
      isFromGallery: false,
      imageError: null
    }
  },
  computed: {
    ...mapGetters(['isAllowed'])
  },
  methods: {
    cropImage () {
      const cropper = this.$refs[`cropper-${this.inputKey}`]
      if (!cropper) {
        this.croppedImg = null
        return null
      }
      const canvas = cropper.getCroppedCanvas()
      this.croppedImg = canvas ? canvas.toDataURL(this.type) : null
      return this.croppedImg
    },

    resetFileSelector () {
      this.$refs[`input-${this.inputKey}`].value = ''
    },

    async save () {
      if (!this.cropImage()) return
      this.isFromGallery = false
      this.$emit('image-selected', this.croppedImg, this.isFromGallery)
      this.close()
    },

    close () {
      this.$refs[`cropper-${this.inputKey}`].value = ''
      this.croppedImg = null
      this.imageError = null
      this.openModal = false
    },

    // normalise a MIME type to a short image-type token
    typeFromMime (mime) {
      const mimeToType = {
        'image/png': 'png',
        'image/jpeg': 'jpeg',
        'image/jpg': 'jpeg',
        'image/gif': 'gif',
        'image/tiff': 'tiff',
        'image/tif': 'tiff',
        'image/svg+xml': 'svg'
      }
      return mimeToType[(mime || '').trim()] || null
    },

    acceptedTypes () {
      return (this.accept || '').split(',').map(s => this.typeFromMime(s)).filter(Boolean)
    },

    // determine the file's REAL type from its content (magic bytes / signature)
    detectRealImageType (file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          const arr = new Uint8Array(event.target.result)
          const hex = Array.from(arr.subarray(0, 4)).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase()
          if (hex.startsWith('89504E47')) {
            resolve('png')
          } else if (hex.startsWith('FFD8FF')) {
            resolve('jpeg')
          } else if (hex.startsWith('47494638')) {
            resolve('gif')
          } else if (hex.startsWith('49492A00') || hex.startsWith('4D4D002A')) {
            resolve('tiff')
          } else {
            const text = String.fromCharCode.apply(null, arr).replace(/\0/g, '')
            if (/<svg[\s>]/i.test(text)) {
              resolve('svg')
            } else {
              resolve(null)
            }
          }
        }
        reader.onerror = () => resolve(null)
        reader.readAsArrayBuffer(file.slice(0, 512))
      })
    },

    async setImage (e) {
      const file = e.target.files[0]
      if (!file) {
        return
      }
      this.imageError = null
      if (file.type.indexOf('image/') === -1) {
        alert(this.$t('error_please_select_image_file'))
        return
      }
      if (this.maxFileMegabytes && file.size > (this.maxFileMegabytes * 1024 * 1024)) {
        this.imageError = this.$t('error_file_too_large', { maxSize: Math.round(this.maxFileMegabytes) })
        return
      }
      // block fake/spoofed/hacked files: the real content must match the file's extension
      const realType = await this.detectRealImageType(file)
      if (!realType || realType !== this.typeFromMime(file.type) || !this.acceptedTypes().includes(realType)) {
        this.imageError = this.$t('st_gallery_unsupported_file_upload')
        return
      }
      this.type = file.type
      if (typeof FileReader === 'function') {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.imgSrc = event.target.result

          if (this.type !== 'image/svg+xml') {
            this.openModal = true
            setTimeout(() => {
              this.$refs[`cropper-${this.inputKey}`].replace(event.target.result)
              this.imageError = null
            }, 200)
          } else {
            this.openModal = false
            this.$emit('image-selected', this.imgSrc)
          }
        }
        reader.readAsDataURL(file)
      } else {
        alert(this.$t('error_filereader_not_supported'))
      }
    },

    selectFile () {
      const input = document.getElementById(this.inputKey)

      input.click()
    },

    openSelectImageDialog () {
      this.imageError = null
      this.displaySelectImageDialog = true
    },

    closeSelectImageDialog () {
      this.displaySelectImageDialog = false
    },

    handleGalleryItemSelected (item) {
      this.isFromGallery = true
      this.$emit('image-selected', item, this.isFromGallery)
    }
  }
}
</script>

<style lang="sass" scoped>

input
  display: none

</style>
